// API Auth Service: wraps calls to our Express backend
import tokenService from './tokenService';

// Get the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

async function jsonRequest(path, options = {}) {
  // Use explicit URL construction
  const url = `${API_BASE_URL}/auth${path}`;
  
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...options,
  });
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// Helper function for authenticated requests
async function authenticatedRequest(path, options = {}) {
  // Ensure we have a valid token before making the request
  const isTokenValid = await tokenService.ensureValidToken();
  
  if (!isTokenValid) {
    throw new Error('Authentication required. Please log in again.');
  }

  // Get authorization header
  const authHeader = tokenService.getAuthHeader();
  
  // Use explicit URL construction
  const url = `${API_BASE_URL}/auth${path}`;
  
  const res = await fetch(url, {
    headers: { 
      'Content-Type': 'application/json',
      ...authHeader
    },
    ...options,
  });
  
  // Check if token is expiring soon
  if (res.headers.get('X-Token-Expiring-Soon')) {
    // Token is about to expire, refresh it in the background
    tokenService.refreshToken().catch(err => 
      console.error('Background token refresh failed:', err)
    );
  }
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// Event listeners for auth state changes
const authStateListeners = new Set();

const apiAuthService = {
  // Email sign-up
  signUpEmail: ({ email, password, fullName }) =>
    jsonRequest('/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName }),
    }),

  // Username sign-up
  signUpUsername: ({ username, password, fullName }) =>
    jsonRequest('/signup', {
      method: 'POST',
      body: JSON.stringify({ username, password, full_name: fullName, isUsernameAuth: true }),
    }),

  // Email sign-in
  signInEmail: async ({ email, password }) => {
    const result = await jsonRequest('/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    // Store tokens in our token service
    if (result.session) {
      tokenService.saveTokens({
        accessToken: result.session.access_token,
        refreshToken: result.session.refresh_token,
        expiresAt: result.session.expires_at
      });
      
      // Notify listeners of auth state change
      authStateListeners.forEach(listener => listener('SIGNED_IN', result));
    }
    
    return result;
  },

  // Username sign-in
  signInUsername: async ({ username, password }) => {
    const result = await jsonRequest('/signin', {
      method: 'POST',
      body: JSON.stringify({ username, password, isUsernameAuth: true }),
    });
    
    // Store tokens in our token service
    if (result.session) {
      tokenService.saveTokens({
        accessToken: result.session.access_token,
        refreshToken: result.session.refresh_token,
        expiresAt: result.session.expires_at
      });
      
      // Notify listeners of auth state change
      authStateListeners.forEach(listener => listener('SIGNED_IN', result));
    }
    
    return result;
  },

  resetPassword: (email) =>
    jsonRequest('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  updatePassword: (password) =>
    authenticatedRequest('/update-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  signOut: async () => {
    // Get the current token before clearing it
    const token = tokenService.getAccessToken();
    
    // Clear tokens from storage
    tokenService.clearTokens();
    
    // Notify the server if we have a token
    if (token) {
      try {
        await jsonRequest('/signout', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
      } catch (error) {
        console.error('Error during server signout:', error);
        // Continue even if server-side logout fails
      }
    }
    
    // Notify listeners of auth state change
    authStateListeners.forEach(listener => listener('SIGNED_OUT', null));
    
    return { success: true };
  },
  
  // Validate the current token
  validateToken: () => tokenService.validateToken(),
  
  // Refresh the current token
  refreshToken: () => tokenService.refreshToken(),
  
  // Get current session
  getSession: async () => {
    try {
      // First try to get session from token service
      const accessToken = tokenService.getAccessToken();
      
      if (accessToken) {
        // Validate the token
        const validationResult = await tokenService.validateToken();
        
        if (validationResult.success) {
          return {
            data: {
              session: {
                access_token: accessToken,
                refresh_token: tokenService.getRefreshToken(),
                user: validationResult.user
              }
            },
            error: null
          };
        }
      }
      
      // If no valid token, check with the server
      const result = await jsonRequest('/session');
      return {
        data: {
          session: result.session
        },
        error: null
      };
    } catch (error) {
      console.error('Error getting session:', error);
      return {
        data: { session: null },
        error
      };
    }
  },
  
  // Sign in with OAuth
  signInWithOAuth: async (options) => {
    try {
      // Start OAuth flow on the server
      const result = await jsonRequest('/oauth', {
        method: 'POST',
        body: JSON.stringify(options)
      });
      
      // Redirect to the OAuth URL provided by the server
      window.location.href = result.url;
      
      // Return a placeholder since we're redirecting
      return { data: {}, error: null };
    } catch (error) {
      console.error('Error starting OAuth flow:', error);
      return { data: null, error };
    }
  },
  
  // Set session (used after OAuth callback or token refresh)
  setSession: async (session) => {
    if (!session) {
      return { error: 'No session provided' };
    }
    
    try {
      // Store tokens in our token service
      tokenService.saveTokens({
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        expiresAt: session.expires_at
      });
      
      // Notify listeners of auth state change
      authStateListeners.forEach(listener => listener('TOKEN_REFRESHED', { session }));
      
      return { data: { session }, error: null };
    } catch (error) {
      console.error('Error setting session:', error);
      return { error };
    }
  },
  
  // Listen for auth state changes
  onAuthStateChange: (callback) => {
    // Add the callback to our listeners
    authStateListeners.add(callback);
    
    // Return an object with unsubscribe method
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            authStateListeners.delete(callback);
          }
        }
      }
    };
  }
};

export default apiAuthService; 